import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ListPage from "../../../components/ListPage";
import SearchInput from "../../../components/filters/SearchInput";
import EmptyValue from "../../../components/EmptyValue";
import useMedicineCatalog from "../../hooks/useMedicineCatalog";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";

// Pharmacist Inventory — the medicine catalog.
//
// Read-only in this pass. The prototype has an add/edit modal, but the catalog is
// shared master data that the legacy Medicine Master page already writes to
// (src/pages/medicine-master), and a second write path into the same table is worth
// designing deliberately rather than porting from a mock. Reading here first means the
// pharmacist can look a medicine up without leaving the console, which is the common
// case; editing stays where it already works.
//
// EXCEPTION, made deliberately, not an oversight of the above (§Phase-7-UI,
// 2026-09-09): the Procurement screen's "Set strip size" input DOES write to `items`
// — but only `items.size`, one field, nowhere else. 2,302 of 3,339 items with live
// demand have `items.size` NULL and are unorderable as a result (no strip size means
// no buffer, no rounding, no suggested quantity). The pharmacist ordering a medicine —
// looking straight at the vendor/quantity/status of that exact row — is exactly who
// knows its real pack size, and blocking procurement on a data-entry gap elsewhere is
// worse than one narrow, purpose-built write path for this one field. See
// setItemStripSize (classes/InventoryProcurement/Functions.js, pillup-api) for the
// full reasoning and the same note repeated there.

const PARAM = { q: "q", page: "page" };

function PharmacyInventoryPage() {
    const [searchParams, setSearchParams] = useSearchParams();

    const q = searchParams.get(PARAM.q) || "";
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    const query = useMedicineCatalog({ search: q, page, size: DEFAULT_PAGE_SIZE });
    const pagination = query.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };
    const rows = query.data?.medicines ?? [];

    const updateParams = (changes, { resetPage = true } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
        });
        if (resetPage) next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    const columns = useMemo(
        () => [
            { key: "id", label: "ID", render: (row) => <span className="font-sans font-medium text-elm-700">{row.id}</span> },
            {
                key: "name",
                label: "Name",
                render: (row) => (
                    <span className="font-semibold text-[var(--ops-carbon-900)]">
                        {row.name || <EmptyValue />}
                    </span>
                ),
            },
            // Wrapped, not nowrap: a combination drug's generic name is the full
            // ingredient list ("Methylcobalamin (1500mcg) + Folic Acid (5mg) + …"),
            // which on one line measured 2000px and pushed the table to 3149px — 40%
            // visible for a seven-column view. It is also the column a pharmacist most
            // needs to read in full, so it wraps rather than being truncated.
            {
                key: "genericName",
                label: "Generic Name",
                wrap: true,
                // `wrap` alone is not enough: DataTable's table is `w-max`, so it sizes
                // to content and a long single line still stretches the column (2000px
                // measured). The explicit max-width is what actually makes it wrap.
                render: (row) =>
                    row.genericName ? (
                        <span className="block max-w-[420px]">{row.genericName}</span>
                    ) : (
                        <EmptyValue />
                    ),
            },
            // "manufacture" is the API's own field name, not a typo.
            { key: "manufacture", label: "Manufacturer" },
            { key: "medicineType", label: "Type" },
            { key: "mnemonic", label: "Mnemonic" },
            // `inventoryId` is deliberately omitted: it holds an opaque encoded token
            // ("OGZBimjq22kx9Nt4E8Eocw=="), not a human-readable code, so it costs 225px
            // of width to show something nobody can act on.
        ],
        []
    );

    const filters = (
        <section
            aria-label="Filters"
            className="rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-4"
        >
            <div className="w-full max-w-[400px]">
                <p className="mb-1 text-xs font-medium text-[var(--ops-carbon-500)]">Search</p>
                <SearchInput
                    value={q}
                    onChange={(value) => updateParams({ [PARAM.q]: value })}
                    placeholder="Medicine name"
                />
            </div>
        </section>
    );

    return (
        <ListPage
            testId="pharmacy-inventory"
            title="Inventory"
            subtitle="The medicine catalog. Read-only here — add and edit medicines from Medicine Master."
            filters={filters}
            columns={columns}
            rows={rows}
            query={query}
            pagination={pagination}
            onPageChange={(nextPage) => updateParams({ [PARAM.page]: String(nextPage) }, { resetPage: false })}
            emptyTitle="No medicines match"
            emptyHint="Try a different name."
        />
    );
}

export default PharmacyInventoryPage;
